import CloseIcon from "@mui/icons-material/CloseRounded";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

import type { UploadFormatDialogState } from "@/common/components/layout/components/dropzone/hooks/useUploadFormatPrompt";
import { formatFileSize, splitFileName } from "@/common/helper/fileDownloadHelper";
import { Trans, useTranslation } from "@/translations";

interface InnerProps {
  files: File[];
  onCancel: () => void;
  onConfirm: (convertToPdf: boolean) => void;
}

function fileTypeLabel(fileName: string): string {
  return splitFileName(fileName).extension.slice(1).toUpperCase();
}

function UploadFormatDialogInner({ files, onCancel, onConfirm }: InnerProps) {
  const { t } = useTranslation();

  return (
    <Dialog open onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle
        variant="h1"
        sx={{ p: 5, pb: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        {t("dropzone.uploadFormat.title")}
        <IconButton onClick={onCancel} aria-label={t("common.close")}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 5 }}>
        <Stack spacing={3}>
          <Stack component="ul" spacing={0.5} sx={{ listStyle: "none", m: 0, p: 0 }}>
            {files.map((file, index) => (
              <Typography component="li" variant="body1" key={`${file.name}-${index}`}>
                <Trans
                  i18nKey="dropzone.uploadFormat.fileEntry"
                  values={{ name: file.name, type: fileTypeLabel(file.name), size: formatFileSize(file.size) }}
                  components={{ strong: <strong /> }}
                />
              </Typography>
            ))}
          </Stack>
          <Typography variant="body1">{t("dropzone.uploadFormat.notice")}</Typography>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{ p: 5, pt: 0, gap: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <Button variant="outlined" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="outlined" onClick={() => onConfirm(false)}>
            {t("dropzone.uploadFormat.uploadOriginal")}
          </Button>
          <Button variant="contained" onClick={() => onConfirm(true)}>
            {t("dropzone.uploadFormat.convertToPdf")}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export function UploadFormatDialog({ state }: { state: UploadFormatDialogState }) {
  if (!state.files) {
    return null;
  }

  return <UploadFormatDialogInner files={state.files} onConfirm={state.onConfirm} onCancel={state.onCancel} />;
}
